import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import Modal from '../../../components/Modal';
import ApiInspector from '../../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import BopExcelTable, { BopExcelColumn } from '../../../components/BopExcelTable';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { API_DOCS } from './CausaliContabiliPage.api';
import './CausaliContabili.css';

const MODULO = 'amministrazione.causali_contabili';

type TipoCausale =
  | 'FATTURA_VENDITA' | 'FATTURA_ACQUISTO' | 'NOTA_CREDITO_EMESSA' | 'NOTA_CREDITO_RICEVUTA'
  | 'INCASSO_CLIENTE' | 'PAGAMENTO_FORNITORE' | 'GIROCONTO' | 'RATEO_RISCONTO'
  | 'APERTURA' | 'CHIUSURA' | 'STIPENDI' | 'AMMORTAMENTI' | 'RITENUTE'
  | 'RIVALUTAZIONI' | 'RIBALTAMENTI' | 'ALTRO';

export const TIPO_CAUSALE_LABEL: Record<TipoCausale, string> = {
  FATTURA_VENDITA: 'Fattura vendita',
  FATTURA_ACQUISTO: 'Fattura acquisto',
  NOTA_CREDITO_EMESSA: 'Nota credito emessa',
  NOTA_CREDITO_RICEVUTA: 'Nota credito ricevuta',
  INCASSO_CLIENTE: 'Incasso cliente',
  PAGAMENTO_FORNITORE: 'Pagamento fornitore',
  GIROCONTO: 'Giroconto',
  RATEO_RISCONTO: 'Rateo / Risconto',
  APERTURA: 'Apertura',
  CHIUSURA: 'Chiusura',
  STIPENDI: 'Stipendi',
  AMMORTAMENTI: 'Ammortamenti',
  RITENUTE: 'Ritenute',
  RIVALUTAZIONI: 'Rivalutazioni',
  RIBALTAMENTI: 'Ribaltamenti',
  ALTRO: 'Altro',
};

interface CausaleSummary {
  id: number;
  societaId: number;
  codice: string;
  descrizione: string;
  tipo: TipoCausale;
  gestisceIva: boolean;
  sezionaleIvaDefaultId: number | null;
  sezionaleIva: { id: number; codice: string; descrizione: string } | null;
  registroContabileDefaultId: number | null;
  registroContabile: { id: number; codice: string; descrizione: string; tipo: string } | null;
  isDefault: boolean;
  attivo: boolean;
  note: string | null;
  nRighe: number;
  daCompletare: boolean;
  dettagliDaCompletare: {
    righeFissoSenzaConto: number;
    mancaSezionale: boolean;
    mancaRegistro: boolean;
  };
}

interface ModalCreaState {
  codice: string;
  descrizione: string;
  tipo: TipoCausale;
  gestisceIva: boolean;
  note: string;
}

export default function CausaliContabiliPage() {
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');
  const canDelete = can(MODULO, 'delete');
  const navigate = useNavigate();

  const { societaId, setSocietaId, listaSocieta, isUnica } = useFiltroSocieta();

  const [items, setItems] = useState<CausaleSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mostraDisattivi, setMostraDisattivi] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState<'' | TipoCausale>('');
  const [filtroDaCompletare, setFiltroDaCompletare] = useState(false);
  const [ricerca, setRicerca] = useState('');

  const [modal, setModal] = useState<ModalCreaState | null>(null);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!societaId) { setItems([]); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      // @api GET /api/causali-contabili?societaId= -> { success: true, data: [{ id, codice, descrizione, tipo, gestisceIva, sezionaleIvaDefaultId, sezionaleIva: { codice, descrizione }, registroContabileDefaultId, registroContabile: { codice, descrizione }, isDefault, attivo, note, nRighe, daCompletare, dettagliDaCompletare }] }
      const res = await apiFetch<{ success: boolean; data: CausaleSummary[] }>(`/api/causali-contabili?societaId=${societaId}`);
      setItems(res.data ?? []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [societaId]);

  const filtered = useMemo(() => {
    const q = ricerca.trim().toLowerCase();
    return items.filter(c => {
      if (!mostraDisattivi && !c.attivo) return false;
      if (filtroTipo && c.tipo !== filtroTipo) return false;
      if (filtroDaCompletare && !c.daCompletare) return false;
      if (q && !(c.codice.toLowerCase().includes(q) || c.descrizione.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [items, ricerca, filtroTipo, filtroDaCompletare, mostraDisattivi]);

  const openCreate = () => {
    setModal({ codice: '', descrizione: '', tipo: 'FATTURA_VENDITA', gestisceIva: true, note: '' });
    setFormError('');
  };
  const closeModal = () => { setModal(null); setFormError(''); };

  const salva = async () => {
    if (!modal) return;
    const codice = modal.codice.trim();
    const descrizione = modal.descrizione.trim();
    if (!codice) { setFormError('Codice obbligatorio'); return; }
    if (!/^[A-Za-z0-9_-]+$/.test(codice)) { setFormError('Il codice può contenere solo lettere, numeri, "-" e "_"'); return; }
    if (!descrizione) { setFormError('Descrizione obbligatoria'); return; }

    setSaving(true);
    setFormError('');
    try {
      // @api POST /api/causali-contabili { societaId, codice, descrizione, tipo, gestisceIva, note } -> { success: true, data: { id } }
      const res = await apiFetch<{ success: boolean; data: { id: number } }>('/api/causali-contabili', {
        method: 'POST',
        body: JSON.stringify({
          societaId: Number(societaId),
          codice,
          descrizione,
          tipo: modal.tipo,
          gestisceIva: modal.gestisceIva,
          note: modal.note.trim() || null,
        }),
      });
      const newId = res.data.id;
      closeModal();
      navigate(`/amministrazione/causali-contabili/${newId}`);
    } catch (e: any) {
      setFormError(e.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const toggleAttivo = async (c: CausaleSummary) => {
    try {
      // @api PATCH /api/causali-contabili/:id/stato { attivo } -> { success: true }
      await apiFetch(`/api/causali-contabili/${c.id}/stato`, {
        method: 'PATCH',
        body: JSON.stringify({ attivo: !c.attivo }),
      });
      load();
    } catch (e: any) {
      setError(e.message || 'Errore nel cambio stato');
    }
  };

  const elimina = async (c: CausaleSummary) => {
    if (!window.confirm(`Eliminare la causale ${c.codice} — ${c.descrizione}?`)) return;
    try {
      // @api DELETE /api/causali-contabili/:id -> { success: true }
      await apiFetch(`/api/causali-contabili/${c.id}`, { method: 'DELETE' });
      load();
    } catch (e: any) {
      window.alert(e.message || 'Errore nell\'eliminazione');
    }
  };

  const noSocieta = !societaId;

  const columns = useMemo<BopExcelColumn<CausaleSummary>[]>(() => [
    {
      key: 'codice',
      header: 'Codice',
      accessor: (c) => c.codice,
      filter: { type: 'text' },
      width: '140px',
      cellClassName: 'cc-codice',
      render: (c) => (
        <>
          {c.codice}
          {c.isDefault && <span className="cc-badge-default" title="Default per tipo">★</span>}
        </>
      ),
    },
    {
      key: 'descrizione',
      header: 'Descrizione',
      accessor: (c) => c.descrizione,
      filter: { type: 'text' },
      render: (c) => (
        <div className="cc-descrizione-cell">
          <span>{c.descrizione}</span>
          {c.daCompletare && (
            <span className="cc-badge-todo" title={
              [
                c.dettagliDaCompletare.righeFissoSenzaConto > 0 ? `${c.dettagliDaCompletare.righeFissoSenzaConto} righe FISSO senza conto` : null,
                c.dettagliDaCompletare.mancaSezionale ? 'manca sezionale IVA default' : null,
                c.dettagliDaCompletare.mancaRegistro ? 'manca registro default' : null,
              ].filter(Boolean).join('; ')
            }>Da completare</span>
          )}
        </div>
      ),
    },
    {
      key: 'tipo',
      header: 'Tipo',
      accessor: (c) => c.tipo,
      filter: {
        type: 'enum',
        options: (Object.keys(TIPO_CAUSALE_LABEL) as TipoCausale[])
          .map(t => ({ value: t, label: TIPO_CAUSALE_LABEL[t] })),
      },
      width: '170px',
      render: (c) => <span className="cc-tipo">{TIPO_CAUSALE_LABEL[c.tipo]}</span>,
    },
    {
      key: 'iva',
      header: 'IVA',
      accessor: (c) => c.gestisceIva ? 'SI' : 'NO',
      filter: {
        type: 'enum',
        options: [
          { value: 'SI', label: 'Sì' },
          { value: 'NO', label: 'No' },
        ],
      },
      width: '90px',
      render: (c) => c.gestisceIva
        ? <span className="bop-badge bop-badge--info">IVA</span>
        : <span className="bop-text-muted">—</span>,
    },
    {
      key: 'default',
      header: 'Sezionale / Registro default',
      accessor: (c) => `${c.sezionaleIva?.codice || ''} ${c.registroContabile?.codice || ''}`.trim(),
      filter: { type: 'text' },
      render: (c) => (
        <div className="cc-default-cell">
          {c.sezionaleIva ? (
            <span className="cc-default-row">Sez: <strong>{c.sezionaleIva.codice}</strong> {c.sezionaleIva.descrizione}</span>
          ) : c.gestisceIva ? <span className="bop-text-muted">sezionale da impostare</span> : null}
          {c.registroContabile ? (
            <span className="cc-default-row">Reg: <strong>{c.registroContabile.codice}</strong> {c.registroContabile.descrizione}</span>
          ) : <span className="bop-text-muted">registro da impostare</span>}
        </div>
      ),
    },
    {
      key: 'nRighe',
      header: 'Righe',
      accessor: (c) => c.nRighe,
      filter: { type: 'number' },
      width: '90px',
      headerClassName: 'bop-text-center',
      cellClassName: 'bop-text-center',
      render: (c) => c.nRighe,
    },
    {
      key: 'attivo',
      header: 'Stato',
      accessor: (c) => c.attivo ? 'ATTIVA' : 'DISATTIVA',
      filter: {
        type: 'enum',
        options: [
          { value: 'ATTIVA',    label: 'Attiva' },
          { value: 'DISATTIVA', label: 'Disattiva' },
        ],
      },
      width: '110px',
      render: (c) => (
        <span className={`bop-badge ${c.attivo ? 'bop-badge--success' : 'bop-badge--neutral'}`}>
          {c.attivo ? 'Attiva' : 'Disattiva'}
        </span>
      ),
    },
  ], []);

  return (
    <Layout>
      <div className="causali-contabili">
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2>Causali contabili</h2>
            <p>Template di scrittura in partita doppia — gestiscono i conti delle righe Dare/Avere generate dalle scritture e dai documenti fiscali</p>
          </div>
          {canEdit && (
            <div className="bop-flex">
              <button className="bop-btn bop-btn-primary" onClick={openCreate} disabled={noSocieta}>+ Nuova causale</button>
            </div>
          )}
        </div>

        <div className="bop-filter-bar">
          {!isUnica && (
            <select className="bop-select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
              <option value="">Seleziona società…</option>
              {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          )}
          <input
            className="bop-input cc-search"
            placeholder="Cerca per codice o descrizione…"
            value={ricerca}
            onChange={e => setRicerca(e.target.value)}
          />
          <select className="bop-select" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value as any)}>
            <option value="">Tutti i tipi</option>
            {(Object.keys(TIPO_CAUSALE_LABEL) as TipoCausale[]).map(t => (
              <option key={t} value={t}>{TIPO_CAUSALE_LABEL[t]}</option>
            ))}
          </select>
          <label className="bop-flex cc-checkbox-label">
            <input type="checkbox" checked={filtroDaCompletare} onChange={e => setFiltroDaCompletare(e.target.checked)} />
            <span>Solo da completare</span>
          </label>
          <label className="bop-flex cc-checkbox-label">
            <input type="checkbox" checked={mostraDisattivi} onChange={e => setMostraDisattivi(e.target.checked)} />
            <span>Mostra anche disattivi</span>
          </label>
        </div>

        {error && <div className="bop-inline-error">{error}</div>}

        {noSocieta ? (
          <div className="bop-card">
            <div className="bop-empty-state">Seleziona una società per gestire le sue causali contabili.</div>
          </div>
        ) : (
          <BopExcelTable<CausaleSummary>
            data={filtered}
            loading={loading}
            rowKey={(c) => c.id}
            onRowClick={(c) => navigate(`/amministrazione/causali-contabili/${c.id}`)}
            initialSort={{ key: 'codice', dir: 'asc' }}
            emptyState={
              <div className="bop-empty-state">
                {items.length === 0
                  ? 'Nessuna causale presente. Verranno create automaticamente alla prima apertura della pagina.'
                  : 'Nessun risultato per i filtri impostati.'}
              </div>
            }
            columns={columns}
            rowActions={(c) => (
              <BopRowActions actions={[
                { icon: <RowIcon.Disable />, label: c.attivo ? 'Disattiva' : 'Riattiva', onClick: () => toggleAttivo(c), hidden: !canEdit },
                { icon: <RowIcon.Trash />, label: 'Elimina', onClick: () => elimina(c), variant: 'danger', hidden: !canDelete },
              ]} />
            )}
          />
        )}
      </div>

      {modal && (
        <Modal
          title="Nuova causale contabile"
          onClose={closeModal}
          footer={
            <>
              <button className="bop-btn bop-btn-secondary" onClick={closeModal}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={salva} disabled={saving}>
                {saving ? 'Salvo…' : 'Salva e configura righe'}
              </button>
            </>
          }
        >
          {formError && <div className="bop-inline-error">{formError}</div>}

          <p className="cc-hint">
            Dopo il salvataggio verrai portato alla pagina di dettaglio della causale per definire le righe Dare/Avere.
          </p>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Codice *</label>
              <input
                className="bop-input"
                value={modal.codice}
                onChange={e => setModal(m => m && { ...m, codice: e.target.value })}
                placeholder="es. FAT_VEN, CUSTOM_01"
                autoFocus
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Tipo *</label>
              <select
                className="bop-select"
                value={modal.tipo}
                onChange={e => setModal(m => m && { ...m, tipo: e.target.value as TipoCausale })}
              >
                {(Object.keys(TIPO_CAUSALE_LABEL) as TipoCausale[]).map(t => (
                  <option key={t} value={t}>{TIPO_CAUSALE_LABEL[t]}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Descrizione *</label>
            <input
              className="bop-input"
              value={modal.descrizione}
              onChange={e => setModal(m => m && { ...m, descrizione: e.target.value })}
              placeholder="es. Fattura vendita 22%, Pagamento fornitore generico…"
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-flex cc-checkbox-label">
              <input
                type="checkbox"
                checked={modal.gestisceIva}
                onChange={e => setModal(m => m && { ...m, gestisceIva: e.target.checked })}
              />
              <span>La causale gestisce IVA (richiede sezionale IVA default)</span>
            </label>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Note</label>
            <textarea
              className="bop-input"
              rows={2}
              value={modal.note}
              onChange={e => setModal(m => m && { ...m, note: e.target.value })}
            />
          </div>
        </Modal>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
